import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import { api } from '@/lib/api'
import type { ApiResponse, PaginatedResponse } from '@/types'

/**
 * EP-160 T160-201w: Settings -> ความช่วยเหลือจาก FlowRent
 * (docs/ep-160-support-access-and-privacy-in-the-app.md §2.1, S2).
 * The owner's time-boxed permission for the FlowRent team to help in the
 * account, the team's request for one, and the history of what the team did.
 * The owner writes; an admin reads.
 */
export const SUPPORT_ACCESS_SCOPES = ['read', 'setup', 'tenants'] as const
export type SupportAccessScope = (typeof SUPPORT_ACCESS_SCOPES)[number]

// D1: 1, 7 or 30 days; the default is 1 day and the default scope is `read`.
export const SUPPORT_ACCESS_DURATIONS = [1, 7, 30] as const
export type SupportAccessDuration = (typeof SUPPORT_ACCESS_DURATIONS)[number]

export interface SupportAccessGrant {
  id: number
  scope: SupportAccessScope
  source: 'settings' | 'request' | 'signup'
  instruction: string
  property_ids: number[]
  all_properties: boolean
  status: 'live' | 'revoked' | 'expired'
  expires_at: string
  remaining_seconds: number
  revoked_at: string | null
  request_id: number | null
  created_at: string
}

export interface SupportGrantRequest {
  id: number
  // FlowRent staff are shown by name only, never by email.
  admin_name: string | null
  reason: string
  suggested_scope: SupportAccessScope
  status: 'pending' | 'approved' | 'declined' | 'expired'
  expires_at: string
  decided_at: string | null
  created_at: string
}

export interface SupportAccessState {
  grant: SupportAccessGrant | null
  request: SupportGrantRequest | null
  recent_grants: SupportAccessGrant[]
}

export interface SupportSessionRow {
  id: number
  admin_name: string | null
  started_at: string
  ended_at: string | null
  item_count: number
  // EP-160 T160-202e: the support case the session was opened for.
  case?: { reason: string; ticket_ref: string | null } | null
}

export interface SupportSummaryItem {
  action: 'create' | 'import' | 'update' | 'destroy'
  resource: string
  count: number
}

export interface SupportAccessEventRow {
  id: number
  actor_name: string
  resource: string
  // EP-160 T160-202e: the kind of record (SupportAccessSummary::RESOURCES or
  // "other") and its name in the account's language, shown instead of the
  // raw controller#action.
  resource_key?: string
  resource_label?: string
  action: 'read' | 'write'
  subject_count: number
  created_at: string
}

export interface SupportSessionHistory {
  session: SupportSessionRow
  summary: { items: SupportSummaryItem[]; total: number }
  events: SupportAccessEventRow[]
}

export const supportAccessKeys = {
  all: ['supportAccess'] as const,
  grant: () => [...supportAccessKeys.all, 'grant'] as const,
  sessions: (page: number) => [...supportAccessKeys.all, 'sessions', page] as const,
  session: (id: number) => [...supportAccessKeys.all, 'session', id] as const,
}

export function useSupportAccessGrant() {
  return useQuery({
    queryKey: supportAccessKeys.grant(),
    queryFn: async () => {
      const { data } = await api.get<ApiResponse<SupportAccessState>>('/api/support_access_grant')
      return data.data
    },
  })
}

export interface CreateSupportAccessGrantInput {
  scope: SupportAccessScope
  days: SupportAccessDuration
  instruction: string
  // Empty means every building.
  property_ids: number[]
  // Set when the grant answers a request from the FlowRent team.
  request_id?: number
}

export function useCreateSupportAccessGrant() {
  const queryClient = useQueryClient()
  return useMutation({
    mutationFn: async (input: CreateSupportAccessGrantInput) => {
      const { data } = await api.post<ApiResponse<SupportAccessGrant>>('/api/support_access_grant', input)
      return data.data
    },
    // A refusal (grant_active, request_expired) also means the card is stale.
    onSettled: () => queryClient.invalidateQueries({ queryKey: supportAccessKeys.grant() }),
  })
}

export function useRevokeSupportAccessGrant() {
  const queryClient = useQueryClient()
  return useMutation({
    mutationFn: async () => {
      const { data } = await api.delete<ApiResponse<SupportAccessGrant>>('/api/support_access_grant')
      return data.data
    },
    onSettled: () => queryClient.invalidateQueries({ queryKey: supportAccessKeys.grant() }),
  })
}

export function useDeclineSupportGrantRequest() {
  const queryClient = useQueryClient()
  return useMutation({
    mutationFn: async (id: number) => {
      const { data } = await api.patch<ApiResponse<{ request: SupportGrantRequest; grant: null }>>(
        `/api/support_grant_requests/${id}`,
        { decision: 'decline' },
      )
      return data.data
    },
    onSettled: () => queryClient.invalidateQueries({ queryKey: supportAccessKeys.grant() }),
  })
}

export function useSupportSessions(page = 1) {
  return useQuery({
    queryKey: supportAccessKeys.sessions(page),
    queryFn: async () => {
      const { data } = await api.get<PaginatedResponse<SupportSessionRow[]>>('/api/support_access_events', {
        params: { page },
      })
      return data
    },
  })
}

export function useSupportSessionHistory(id: number, enabled: boolean) {
  return useQuery({
    queryKey: supportAccessKeys.session(id),
    queryFn: async () => {
      const { data } = await api.get<PaginatedResponse<SupportSessionHistory>>('/api/support_access_events', {
        params: { session_id: id },
      })
      return data
    },
    enabled,
  })
}
